/**
 * Color manipulation utilities for deriving theme colors
 */

/**
 * Converts a hex color to RGB components
 *
 * @param hex - Hex color string (with or without #)
 * @returns RGB components as [r, g, b]
 */
function hexToRgb(hex: string): [number, number, number] | null {
  const cleaned = hex.replace('#', '');
  const parsed = parseInt(cleaned, 16);

  if (isNaN(parsed)) {
    return null;
  }

  return [(parsed >> 16) & 255, (parsed >> 8) & 255, parsed & 255];
}

/**
 * Converts RGB components to hex color
 *
 * @param r - Red component (0-255)
 * @param g - Green component (0-255)
 * @param b - Blue component (0-255)
 * @returns Hex color string with #
 */
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/**
 * Darkens a hex color by a given percentage
 *
 * @param color - Hex color string
 * @param percent - Percentage to darken (0-1, e.g., 0.2 for 20% darker)
 * @returns Darkened hex color string
 */
export function darken(color: string, percent: number): string {
  const rgb = hexToRgb(color);

  if (!rgb) {
    return color;
  }

  const [r, g, b] = rgb;
  const factor = 1 - percent;

  return rgbToHex(
    Math.max(0, Math.round(r * factor)),
    Math.max(0, Math.round(g * factor)),
    Math.max(0, Math.round(b * factor))
  );
}

/**
 * Lightens a hex color by a given percentage
 *
 * @param color - Hex color string
 * @param percent - Percentage to lighten (0-1, e.g., 0.3 for 30% lighter)
 * @returns Lightened hex color string
 */
export function lighten(color: string, percent: number): string {
  const rgb = hexToRgb(color);

  if (!rgb) {
    return color;
  }

  const [r, g, b] = rgb;

  return rgbToHex(
    Math.min(255, Math.round(r + (255 - r) * percent)),
    Math.min(255, Math.round(g + (255 - g) * percent)),
    Math.min(255, Math.round(b + (255 - b) * percent))
  );
}
